import React from 'react';
import {
  Grid,
  Switch,
  Typography,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Button,
  Box,
  Paper
} from '@mui/material';
import { TimePicker, MobileTimePicker } from '@mui/x-date-pickers';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

/**
 * Movement Settings Form Component
 * 
 * Focused component for managing movement/standing reminder settings
 * including time selection, frequency, and day selection.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.settings - Movement settings object
 * @param {Function} props.onUpdateSetting - Callback for updating settings
 * @param {Function} props.onToggleDay - Callback for toggling day selection
 * @param {Function} props.onSave - Callback for saving settings
 * @param {Function} props.onSnoozeToggle - Callback for snooze toggle
 * @param {boolean} props.disabled - Whether form is disabled
 */
const MovementSettingsForm = ({
  settings,
  onUpdateSetting,
  onToggleDay,
  onSave,
  disabled = false
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  // Days of week with translation keys for reminder scheduling
  const dayOptions = [
    { value: 1, label: t('monday') },
    { value: 2, label: t('tuesday') },
    { value: 3, label: t('wednesday') },
    { value: 4, label: t('thursday') },
    { value: 5, label: t('friday') },
    { value: 6, label: t('saturday') },
    { value: 7, label: t('sunday') },
  ];

  // Interval frequency options for movement/standing reminders (in hours)
  // Values represent hours between reminders: 0.5h, 1h, 1.5h, 2h, 2.5h, 3h, 3.5h, 4h
  const frequencyOptions = [
    { value: '0.5', label: t('every30mins') },
    { value: '1', label: t('every60mins') },
    { value: '1.5', label: t('every90mins') },
    { value: '2', label: t('every120mins') },
    { value: '2.5', label: t('every150mins') },
    { value: '3', label: t('every180mins') },
    { value: '3.5', label: t('every210mins') },
    { value: '4', label: t('every240mins') },
  ];

  // Select appropriate time picker component based on device (mobile uses touch-friendly MobileTimePicker)
  const TimePickerComponent = isMobile ? MobileTimePicker : TimePicker;

  /**
   * Handle time field changes (startTime or endTime)
   * Formats selected time from dayjs object to HH:mm string format for API
   * @param {string} field - Field name ('startTime' or 'endTime')
   * @param {dayjs.Dayjs} newValue - Selected time from DatePicker
   */
  const handleTimeChange = (field, newValue) => {
    if (newValue) {
      const formattedTime = newValue.format("HH:mm");
      onUpdateSetting(field, formattedTime);
    }
  };

  return (
    <Paper elevation={1} sx={{ 
      p: isMobile ? 2 : 3,
      maxWidth: '100%',
      mx: 'auto',
      mb: 2, // Add margin bottom for spacing in scroll
      minHeight: isMobile ? '350px' : '400px', // Ensure minimum height for scroll detection
    }} className="movementCard">
      {/* Header Controls */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2 
      }}>
        <FormControlLabel
          control={
            <Switch
              className="movementSwitch"
              checked={settings.isEnabled}
              onChange={(e) => onUpdateSetting('isEnabled', e.target.checked)}
              disabled={disabled}
              color="primary"
            />
          }
          label={
            <Typography variant="subtitle1" fontWeight={600}>
              {settings.isEnabled ? t('reminderEnabled') : t('reminderDisabled')}
            </Typography>
          }
        />
      </Box>

      {/* Time and Frequency Settings */}
      <Grid container spacing={isMobile ? 1 : 2} sx={{ 
        mb: 3,
        maxWidth: '100%',
      }}>
        <Grid item xs={12} sm={4}>
          <TimePickerComponent
            label={t('startTime')}
            ampm={false}
            value={settings.startTime ? dayjs(settings.startTime, "HH:mm") : null}
            onChange={(newValue) => handleTimeChange('startTime', newValue)}
            disabled={disabled || !settings.isEnabled}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputProps: {
                  readOnly: true,
                },
              },
              ...(isMobile ? {} : {
                textField: {
                  onFocus: (e) => {
                    e.target.readOnly = false;
                    e.target.blur();
                    e.target.readOnly = true;
                  },
                  onClick: (e) => {
                    e.target.readOnly = false;
                    e.target.blur();
                    e.target.focus();
                    e.target.readOnly = true;
                  },
                }
              })
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TimePickerComponent
            label={t('endTime')}
            ampm={false}
            value={settings.endTime ? dayjs(settings.endTime, "HH:mm") : null}
            onChange={(newValue) => handleTimeChange('endTime', newValue)}
            disabled={disabled || !settings.isEnabled}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputProps: {
                  readOnly: true,
                },
              },
              ...(isMobile ? {} : {
                textField: {
                  onFocus: (e) => {
                    e.target.readOnly = false;
                    e.target.blur();
                    e.target.readOnly = true;
                  },
                  onClick: (e) => {
                    e.target.readOnly = false;
                    e.target.blur();
                    e.target.focus();
                    e.target.readOnly = true;
                  },
                }
              })
            }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <Select
              value={settings.frequency}
              onChange={(e) => onUpdateSetting('frequency', e.target.value)}
              disabled={disabled || !settings.isEnabled}
              displayEmpty
            >
              {frequencyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Days Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('activeDays')}
        </Typography>
        <FormGroup>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: isMobile ? 0.5 : 1,
            maxWidth: '100%',
          }}>
            {dayOptions.map((day) => (
              <FormControlLabel
                key={day.value}
                control={
                  <Checkbox
                    checked={settings.selectedDays.includes(day.value)}
                    onChange={() => onToggleDay(day.value)}
                    disabled={disabled || !settings.isEnabled}
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {day.label}
                  </Typography>
                }
                sx={{ 
                  mr: 1,
                  ml: 0,
                  '& .MuiTypography-root': { 
                    fontSize: '0.75rem' 
                  }
                }}
              />
            ))}
          </Box>
        </FormGroup>
      </Box>

      {/* Save Button */}
      {settings.isDirty && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={disabled}
            sx={{
              minWidth: 120,
              fontWeight: 600,
            }}
          >
            {t('save')}
          </Button>
        </Box>
      )}

      {/* Settings Summary */}
      {settings.isEnabled && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          backgroundColor: 'primary.50',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'primary.200'
        }}>
          <Typography variant="caption" color="primary.main">
            <strong>{t('summary')}:</strong> {t('movementRemindersEvery')} {
              frequencyOptions.find(f => f.value === settings.frequency)?.label || settings.frequency
            } {t('from')} {settings.startTime} {t('to')} {settings.endTime}
            {settings.selectedDays.length > 0 && (
              <span>
                {' '}{t('on')} {settings.selectedDays.map(dayId => 
                  dayOptions.find(d => d.value === dayId)?.label
                ).join(', ')}
              </span>
            )}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MovementSettingsForm; 